/**
 * @lines98/shared/config/app-routes — ЕДИНЫЙ источник списка ИГРОВЫХ (app-)путей
 * для SEO-исключения (noindex). ADR-0020 Фаза 3 (per-route игровые экраны) +
 * SEO-хардинг (owner 2026-06-19): app-поверхности — НЕ индексируемый контент.
 *
 * ЗАЧЕМ / WHY THIS EXISTS: игровые экраны (`/play`, `/daily`, `/leaderboard`,
 * `/profile`, `/workshop`, `/companions`) — это ИНТЕРАКТИВНЫЕ app-поверхности, а не
 * контент-страницы. Без noindex они = «тонкие дубли» (один и тот же SPA-shell под
 * разными путями), которые конкурируют в выдаче с настоящими контент-страницами
 * (`/daily-board/`, `/leaderboards/` и т.д.). Owner 2026-06-19: исключить их из
 * индекса. Список app-путей нужен СРАЗУ в трёх местах — (1) `robots.txt`
 * (`Disallow`), (2) SPA-страховка `<meta robots noindex>` при монтаже игровой фазы,
 * (3) прод-слой `X-Robots-Tag` (Caddy, deploy-док). Чтобы они НЕ разъехались, путь
 * живёт ОДНИМ массивом ЗДЕСЬ (shared), а не хардкодом в каждом месте:
 *   - SSG (`scripts/build-content/sitemap.ts` → `buildRobots`) импортирует его;
 *   - клиент (`lib/game-routing.ts` `GAME_ROUTE_PATHS`) СТРОИТСЯ из него;
 *   - Caddy-док ссылается на этот же контракт.
 *
 * ⚠ НЕ включает `/r/` (реплей-шаринг — виральный SEO-вход, ДОЛЖЕН краулиться) и НЕ
 * включает контент-пути (`/`, `/<slug>/`, `/<lang>/...` — индексируемые). Только
 * чисто-интерактивные app-направления.
 *
 * SCOPE: канонический список app-путей (origin-relative) + хелпер «является ли путь
 *   app-путём» (терпим к замыкающему «/»). Vanilla, без DOM.
 * NOT IN SCOPE: маппинг path↔phase / store-действия (lib/game-routing — клиент);
 *   контент-каркас (./content-routes); sitemap/robots-сериализация (build-content).
 *
 * Cross-references: ADR-0020 Фаза 3; ADR-0021 addendum-001 §C; deploy-док
 *   `deploy-serving-layer-model-b.md` (X-Robots-Tag контракт); seo-strategy §1.3.
 */
/**
 * Канонические ИГРОВЫЕ (app-)пути — origin-relative, БЕЗ языкового префикса (игровые
 * экраны не локализуются по URL, ADR-0020 D-5/O-2). ЕДИНЫЙ источник: robots.txt
 * Disallow + SPA noindex-meta + клиентский `GAME_ROUTE_PATHS` + Caddy X-Robots-Tag.
 * Порядок не значим (используется как множество). Добавить новое app-направление =
 * добавить путь СЮДА (тогда оно автоматически попадёт во все три SEO-механизма).
 */
export const APP_ROUTE_PATHS = [
    '/play',
    '/daily',
    '/leaderboard',
    '/workshop',
    '/companions',
    '/profile',
    // «Архив игр» (внутренний namespace `book`) — личный каталог реплеев (ADR-0023;
    // replay-collection.md). app-поверхность (интерактивная, per-account), не
    // индексируемый контент → noindex. Видимый URL переименован `/book`→`/archive`
    // (owner «Маршрут — ФИНАЛ» 2026-06-24); старый `/book` 301-редиректит сюда
    // (LEGACY_APP_ROUTE_REDIRECTS). ВНУТРЕННИЙ id `book` (route-key/phase/showBook/
    // .book-*) НЕ переименован — это код-термин.
    '/archive',
    // «Поддержать» (крипто-донат L0, ADR-0024 addendum) — интерактивная app-поверхность
    // (адрес + QR + копирование), не индексируемый контент → noindex/Disallow.
    '/support',
];
/**
 * LEGACY app-пути → их новый канонический адрес (постоянное переименование URL).
 * ЕДИНЫЙ источник правила редиректа: старый адрес НЕ удаляется, а ПЕРЕНАПРАВЛЯЕТСЯ
 * на новый (closed-loop SEO — закладки/индекс/внешние ссылки на старый URL не дают
 * 404, вес консолидируется на новом). Это 301 (permanent): экран переехал навсегда,
 * не временно (ADR-0020 канонизация структуры URL, как `/en/`→`/`). Потребители:
 *   - клиент (`app.ts` boot + popstate): `replaceState` старый→новый ПЕРЕД монтажом
 *     фазы (dev/SPA — HTTP-кодов нет, навигация для разработчика);
 *   - прод-слой Caddy: реальный `redir <old> <new> permanent` (301) — deploy-док
 *     `deploy-serving-layer-model-b.md`.
 *
 * `/gnomes` → `/companions` (owner-decision 2026-06-21): пользовательский экран
 * «Помощников» переименован в Companions на уровне URL + видимой en-подписи.
 * ВНУТРЕННИЙ id `gnomes` (route-key / phase / store-действие / классы / поля) НЕ
 * меняется — это код-термин, а не видимый адрес.
 */
export const LEGACY_APP_ROUTE_REDIRECTS = {
    '/gnomes': '/companions',
    // `/book` → `/archive` (owner «Маршрут — ФИНАЛ» 2026-06-24): видимый адрес
    // направления «Архив игр» (внутренний namespace `book`) переименован. Старый
    // `/book` НЕ удаляется — 301-редиректит на `/archive` (закладки/индекс/popstate
    // не дают 404). Тот же механизм, что `/gnomes`→`/companions`. ВНУТРЕННИЙ id
    // `book` (route-key/phase/showBook/.book-*) — НЕ меняется (код-термин).
    '/book': '/archive',
};
/**
 * Является ли origin-relative `pathname` одним из app-путей (для SPA noindex-страховки).
 * Терпим к замыкающему «/» (History-навигация / ручной ввод). app-пути одноуровневые,
 * совпадение — ПОЛНОЕ (не prefix): `/playthrough` НЕ матчит `/play` (иначе ложные
 * срабатывания). Игнорирует query/hash (caller передаёт уже `location.pathname`).
 */
export function isAppRoutePath(pathname) {
    const clean = pathname.replace(/\/+$/, '') || '/';
    return APP_ROUTE_PATHS.includes(clean);
}
/**
 * Целевой канонический путь для LEGACY app-адреса, либо `null` если путь не legacy.
 * Терпим к замыкающему «/» (как `isAppRoutePath`). Чистая функция (без DOM) —
 * один источник для клиентского `replaceState` (dev) и прод-301 (Caddy).
 */
export function legacyAppRouteRedirect(pathname) {
    const clean = pathname.replace(/\/+$/, '') || '/';
    return LEGACY_APP_ROUTE_REDIRECTS[clean] ?? null;
}
//# sourceMappingURL=app-routes.js.map